/**
 * QA Review Page
 *
 * Spreadsheet-style QA checklist for blog posts.
 * Each row is a blog post; columns are the 13 QA issue flags.
 * Checkboxes toggle instantly via optimistic mutations.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Empty } from 'antd';
import { ExternalLink, Pencil, ArrowLeft, ArrowUp, ArrowDown, AlertCircle, ChevronLeft, ChevronRight, MessageSquare, FileEdit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { useTenantStore } from '@/stores/useTenantStore';
import { useQAReviews, useUpdateQAReview, useBulkMarkReviewed, useBulkClearIssues } from '@/hooks/useQAReviews';
import { useIssueSelection } from '@/hooks/useIssueSelection';
import { QA_RULES, getIssueCount, hasQAReview } from '@/types/qaReview';
import type { QARuleKey, QAReviewWithContent, QAReviewFilters } from '@/types/qaReview';
import { QACheckboxCell } from '@/components/qa-review/QACheckboxCell';
import { QAProgressBadge } from '@/components/qa-review/QAProgressBadge';
import { QANotesDrawer } from '@/components/qa-review/QANotesDrawer';
import { QABulkActionBar } from '@/components/qa-review/QABulkActionBar';
import { QASummaryStats } from '@/components/qa-review/QASummaryStats';
import {
  QAFilterBar,
  DEFAULT_FILTERS,
  type QAFilters,
  type QASortOption,
} from '@/components/qa-review/QAFilterBar';

type ColumnSortDir = 'issues_first' | 'clean_first' | null;

// Sticky column left offsets: checkbox(w-8=32px), #(w-8=32px)
const STICKY_CHECKBOX_LEFT = 'left-0';
const STICKY_NUM_LEFT = 'left-8';        // 32px

function filtersToAPI(filters: QAFilters, page: number): QAReviewFilters {
  const api: QAReviewFilters = {};

  if (filters.qaStatus === 'clean') api.qa_status = 'complete';
  else if (filters.qaStatus === 'has_issues') api.qa_status = 'incomplete';
  else if (filters.qaStatus === 'not_reviewed') api.qa_status = 'not_reviewed';

  if (filters.wpStatus !== 'all') api.wp_status = filters.wpStatus;
  if (filters.search) api.search = filters.search;

  const sortMap: Record<QASortOption, { sort_by: string; sort_order: 'asc' | 'desc' }> = {
    keyword_asc: { sort_by: 'keyword', sort_order: 'asc' },
    date_newest: { sort_by: 'created_at', sort_order: 'desc' },
    most_issues: { sort_by: 'issue_count', sort_order: 'desc' },
    least_issues: { sort_by: 'issue_count', sort_order: 'asc' },
  };
  const sort = sortMap[filters.sort];
  if (sort) {
    api.sort_by = sort.sort_by;
    api.sort_order = sort.sort_order;
  }

  api.limit = filters.pageSize;
  api.offset = (page - 1) * filters.pageSize;

  return api;
}

function getRowBorderClass(review: QAReviewWithContent): string {
  if (!hasQAReview(review)) return 'border-l-2 border-l-transparent';
  const issues = getIssueCount(review);
  if (issues === 0) return 'border-l-2 border-l-green-500';
  return 'border-l-2 border-l-red-400';
}

function getRowBg(review: QAReviewWithContent): string {
  if (!hasQAReview(review)) return '';
  const issues = getIssueCount(review);
  if (issues === 0) return 'bg-green-50 dark:bg-green-950/40/50';
  return 'bg-amber-50 dark:bg-amber-950/40/50';
}

export default function QAReviewPage() {
  const navigate = useNavigate();
  const { currentTenant: tenantId } = useTenantStore();
  const [filters, setFilters] = useState<QAFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [drawerReview, setDrawerReview] = useState<QAReviewWithContent | null>(null);
  const [columnSort, setColumnSort] = useState<{ key: QARuleKey; dir: ColumnSortDir }>({
    key: 'issue_ext_links' as QARuleKey,
    dir: null,
  });

  // Back navigation: prefer browser-back (restores planner URL state + scroll),
  // fall back to the planner table view when there's no history.
  const goBack = useCallback(() => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/content/planner?view=table');
  }, [navigate]);

  // Reset page to 1 when filters change
  const prevFiltersRef = useRef(filters);
  useEffect(() => {
    const prev = prevFiltersRef.current;
    if (
      prev.qaStatus !== filters.qaStatus ||
      prev.wpStatus !== filters.wpStatus ||
      prev.search !== filters.search ||
      prev.sort !== filters.sort ||
      prev.pageSize !== filters.pageSize
    ) {
      setPage(1);
    }
    prevFiltersRef.current = filters;
  }, [filters]);

  const apiFilters = filtersToAPI(filters, page);
  const { data, isLoading, error } = useQAReviews(tenantId || '', apiFilters);
  const updateMutation = useUpdateQAReview();
  const bulkMarkReviewed = useBulkMarkReviewed();
  const bulkClearIssues = useBulkClearIssues();

  const reviews = data?.reviews ?? [];

  const allContentIds = useMemo(
    () => reviews.map((r) => r.content_id),
    [reviews]
  );
  const selection = useIssueSelection(allContentIds);

  // Column totals: count of issues per QA rule across all reviews
  const columnTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    for (const rule of QA_RULES) {
      totals[rule.key] = reviews.reduce(
        (count, r) => count + (r[rule.key as QARuleKey] === true ? 1 : 0),
        0
      );
    }
    return totals;
  }, [reviews]);

  // Sort reviews by selected QA column if active
  const sortedReviews = useMemo(() => {
    if (!columnSort.dir) return reviews;
    const key = columnSort.key;
    return [...reviews].sort((a, b) => {
      const aVal = a[key] === true ? 1 : 0;
      const bVal = b[key] === true ? 1 : 0;
      return columnSort.dir === 'issues_first' ? bVal - aVal : aVal - bVal;
    });
  }, [reviews, columnSort]);

  const handleToggle = useCallback(
    async (contentId: string, ruleKey: QARuleKey, value: boolean) => {
      await updateMutation.mutateAsync({
        contentId,
        tenantId: tenantId || '',
        data: { [ruleKey]: value },
      });
    },
    [updateMutation, tenantId]
  );

  const handleBulkMarkReviewed = useCallback(() => {
    bulkMarkReviewed.mutate(
      { contentIds: selection.selectedIds, tenantId: tenantId || '', reviewedBy: 'admin' },
      { onSuccess: () => selection.clear() }
    );
  }, [bulkMarkReviewed, selection, tenantId]);

  const handleBulkClearIssues = useCallback(() => {
    bulkClearIssues.mutate(
      { contentIds: selection.selectedIds, tenantId: tenantId || '' },
      { onSuccess: () => selection.clear() }
    );
  }, [bulkClearIssues, selection, tenantId]);

  const handleColumnCheckAll = useCallback(
    (ruleKey: QARuleKey, checked: boolean) => {
      for (const review of reviews) {
        const current = review[ruleKey] === true;
        if (current !== checked) {
          updateMutation.mutate({
            contentId: review.content_id,
            tenantId: tenantId || '',
            data: { [ruleKey]: checked },
          });
        }
      }
    },
    [reviews, updateMutation, tenantId]
  );

  const handleColumnHeaderClick = useCallback((ruleKey: QARuleKey) => {
    setColumnSort((prev) => {
      if (prev.key !== ruleKey) return { key: ruleKey, dir: 'issues_first' };
      if (prev.dir === 'issues_first') return { key: ruleKey, dir: 'clean_first' };
      if (prev.dir === 'clean_first') return { key: ruleKey, dir: null };
      return { key: ruleKey, dir: 'issues_first' };
    });
  }, []);

  if (!tenantId) {
    return (
      <div className="p-6">
        <Empty description="Select a workspace to view QA reviews" />
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <div className="p-6">
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={goBack}
              className="gap-1 px-2 text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <h1 className="text-2xl font-bold tracking-tight">Blog QA Review</h1>
            {selection.selectedCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                {selection.selectedCount} selected
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground text-sm mt-1">
            Review blog posts for quality issues. Checked = issue found.
          </p>
          {data && data.reviews.length > 0 && (
            <div className="mt-2">
              <QASummaryStats reviews={data.reviews} />
            </div>
          )}
        </div>

        <QAFilterBar filters={filters} onFiltersChange={setFilters} />

        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <Spin size="large" />
          </div>
        )}

        {error && (
          <div className="p-4 text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-md">
            Failed to load QA reviews. Please try again.
          </div>
        )}

        {data && data.reviews.length === 0 && (
          <Empty description="No blog posts found for this workspace" />
        )}

        {data && reviews.length > 0 && (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <Table className="w-full">
                <TableHeader className="sticky top-0 z-20">
                  <TableRow className="bg-muted/50">
                    {/* Sticky col 1: Checkbox */}
                    <TableHead className={cn('w-8 text-center sticky z-30 bg-muted/50', STICKY_CHECKBOX_LEFT)}>
                      <Checkbox
                        checked={selection.isAllSelected}
                        onCheckedChange={() => selection.toggleAll()}
                        aria-label="Select all rows"
                        className="h-3.5 w-3.5"
                      />
                    </TableHead>
                    {/* Sticky col 2: Row number */}
                    <TableHead className={cn('w-8 text-center sticky z-30 bg-muted/50', STICKY_NUM_LEFT)}>
                      #
                    </TableHead>
                    {/* Col 3: Keyword */}
                    <TableHead className="bg-muted/50">
                      Keyword
                    </TableHead>
                    {/* Other columns */}
                    <TableHead className="w-20 text-center bg-muted/50">Status</TableHead>
                    <TableHead className="w-12 text-center bg-muted/50">Words</TableHead>
                    <TableHead className="w-16 text-center bg-muted/50">Issues</TableHead>
                    <TableHead className="w-28 text-center bg-muted/50">
                      <div className="flex items-center justify-center gap-1">
                        <MessageSquare className="h-3.5 w-3.5" />
                        <span>Notes</span>
                      </div>
                    </TableHead>
                    {QA_RULES.map((rule) => {
                      const isActive = columnSort.key === rule.key && columnSort.dir !== null;
                      const hasIssues = columnTotals[rule.key] > 0;
                      return (
                        <TableHead
                          key={rule.key}
                          className="w-9 h-20 text-center px-0.5 cursor-pointer select-none bg-muted/50 hover:bg-muted/80 transition-colors"
                          onClick={() => handleColumnHeaderClick(rule.key as QARuleKey)}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex flex-col items-center gap-1">
                                <div className="flex items-center gap-0.5 -rotate-45 origin-center whitespace-nowrap">
                                  <span className="text-[11px] font-medium">
                                    {rule.shortLabel}
                                  </span>
                                  {hasIssues && !isActive && (
                                    <AlertCircle className="h-3 w-3 text-red-400 flex-shrink-0" />
                                  )}
                                  {isActive && columnSort.dir === 'issues_first' && (
                                    <ArrowDown className="h-3 w-3 text-foreground flex-shrink-0" />
                                  )}
                                  {isActive && columnSort.dir === 'clean_first' && (
                                    <ArrowUp className="h-3 w-3 text-foreground flex-shrink-0" />
                                  )}
                                </div>
                                <Checkbox
                                  className="h-3 w-3"
                                  onCheckedChange={(checked) => {
                                    handleColumnCheckAll(rule.key as QARuleKey, checked as boolean);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  aria-label={`Check all: ${rule.label}`}
                                />
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-[250px]">
                              <p className="font-medium text-xs">{rule.label}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
                              <p className="text-xs text-muted-foreground mt-1">Click header to sort</p>
                            </TooltipContent>
                          </Tooltip>
                        </TableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedReviews.map((review, index) => (
                    <TableRow
                      key={review.content_id}
                      className={cn(
                        getRowBorderClass(review),
                        getRowBg(review),
                        index % 2 === 1 && 'bg-muted/20',
                        'hover:bg-muted/40 transition-colors',
                        selection.isSelected(review.content_id) && 'bg-primary/5'
                      )}
                    >
                      {/* Sticky col 1: Checkbox */}
                      <TableCell className={cn('text-center sticky z-10 bg-inherit', STICKY_CHECKBOX_LEFT)}>
                        <Checkbox
                          checked={selection.isSelected(review.content_id)}
                          onCheckedChange={() => selection.toggle(review.content_id)}
                          aria-label={`Select ${review.keyword || 'post'}`}
                          className="h-3.5 w-3.5"
                        />
                      </TableCell>
                      {/* Sticky col 2: Row number */}
                      <TableCell
                        className={cn(
                          'text-center text-muted-foreground text-xs cursor-pointer sticky z-10 bg-inherit',
                          STICKY_NUM_LEFT,
                        )}
                        onClick={() => setDrawerReview(review)}
                      >
                        {(page - 1) * filters.pageSize + index + 1}
                      </TableCell>
                      {/* Col 3: Keyword */}
                      <TableCell className="py-1">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => navigate(`/blog/${review.content_id}`)}
                                className="font-medium text-sm truncate text-left hover:underline text-primary"
                                title={review.keyword || ''}
                              >
                                {review.keyword || '(no keyword)'}
                              </button>
                              <button
                                onClick={() => setDrawerReview(review)}
                                className="text-muted-foreground hover:text-foreground flex-shrink-0"
                                title="View notes"
                              >
                                <Pencil className="h-3 w-3" />
                              </button>
                              {review.wordpress_post_url && (
                                <a
                                  href={review.wordpress_post_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-muted-foreground hover:text-foreground flex-shrink-0"
                                  title="View on WordPress"
                                >
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </a>
                              )}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" align="start" className="max-w-[400px]">
                            <p className="text-xs font-medium">{review.title || '(untitled)'}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-center py-1">
                        <WPStatusBadge status={review.publish_status} />
                      </TableCell>
                      <TableCell className="text-center text-xs tabular-nums text-muted-foreground py-1">
                        {review.total_word_count ?? '—'}
                      </TableCell>
                      <TableCell className="text-center py-1">
                        <QAProgressBadge review={review} />
                      </TableCell>
                      {/* Notes cell */}
                      <TableCell className="py-1 px-1.5">
                        <NotesCell review={review} onClick={() => setDrawerReview(review)} />
                      </TableCell>
                      {QA_RULES.map((rule) => (
                        <TableCell key={rule.key} className="px-0 py-1">
                          <QACheckboxCell
                            checked={review[rule.key as QARuleKey]}
                            ruleDescription={rule.description}
                            onToggle={(value) =>
                              handleToggle(review.content_id, rule.key as QARuleKey, value)
                            }
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
                {/* Summary footer: column issue totals */}
                <TableFooter className="sticky bottom-0 z-20">
                  <TableRow className="bg-muted/80">
                    {/* Footer cols */}
                    <TableCell className={cn('sticky z-30 text-center bg-muted/80', STICKY_CHECKBOX_LEFT)} />
                    <TableCell className={cn('sticky z-30 text-center bg-muted/80', STICKY_NUM_LEFT)} />
                    <TableCell className="font-semibold text-xs bg-muted/80">
                      Column Totals
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell className="text-center">
                      <span className="text-xs font-semibold tabular-nums">
                        {reviews.reduce((sum, r) => sum + getIssueCount(r), 0)}
                      </span>
                    </TableCell>
                    <TableCell /> {/* Notes column footer spacer */}
                    {QA_RULES.map((rule) => {
                      const count = columnTotals[rule.key];
                      const pct = reviews.length > 0 ? count / reviews.length : 0;
                      const isHigh = pct > 0.3;
                      return (
                        <TableCell key={rule.key} className="px-0.5 text-center">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span
                                className={cn(
                                  'inline-flex items-center justify-center text-xs font-semibold tabular-nums rounded px-1.5 py-0.5',
                                  isHigh
                                    ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400'
                                    : count > 0
                                      ? 'bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-400'
                                      : 'text-muted-foreground',
                                )}
                              >
                                {count}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">
                                {count} of {reviews.length} posts ({Math.round(pct * 100)}%)
                                {isHigh && ' — high issue rate'}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableFooter>
              </Table>
            </div>
            {/* Pagination bar */}
            {data && (
              <div className="flex items-center justify-between border-t px-4 py-3 bg-muted/30">
                <p className="text-sm text-muted-foreground tabular-nums">
                  Showing{' '}
                  <span className="font-medium text-foreground">
                    {Math.min(data.offset + 1, data.total)}
                  </span>
                  {'-'}
                  <span className="font-medium text-foreground">
                    {Math.min(data.offset + data.limit, data.total)}
                  </span>
                  {' of '}
                  <span className="font-medium text-foreground">{data.total}</span>
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="h-8 px-3"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Previous
                  </Button>
                  <span className="text-sm tabular-nums text-muted-foreground px-2">
                    Page <span className="font-medium text-foreground">{page}</span>
                    {' of '}
                    <span className="font-medium text-foreground">
                      {Math.max(1, Math.ceil(data.total / filters.pageSize))}
                    </span>
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= Math.ceil(data.total / filters.pageSize)}
                    onClick={() => setPage((p) => p + 1)}
                    className="h-8 px-3"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Notes Drawer */}
        <QANotesDrawer
          review={drawerReview}
          open={!!drawerReview}
          onClose={() => setDrawerReview(null)}
          tenantId={tenantId || ''}
        />

        {/* Bulk Action Bar */}
        <QABulkActionBar
          selectedCount={selection.selectedCount}
          onMarkReviewed={handleBulkMarkReviewed}
          onClearIssues={handleBulkClearIssues}
          onClearSelection={selection.clear}
          isLoading={bulkMarkReviewed.isPending || bulkClearIssues.isPending}
        />
      </div>
    </TooltipProvider>
  );
}

function NotesCell({ review, onClick }: { review: QAReviewWithContent; onClick: () => void }) {
  const count = review.comment_count ?? 0;
  const editorCount = review.editor_comment_count ?? 0;
  const latest = review.latest_comment;
  const totalCount = count + editorCount;

  if (totalCount === 0) {
    return (
      <button
        onClick={onClick}
        className="flex items-center justify-center w-full text-muted-foreground/50 hover:text-muted-foreground transition-colors"
        title="Add a note"
      >
        <MessageSquare className="h-3.5 w-3.5" />
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 w-full justify-center"
      title={`${count > 0 ? `${count} QA` : ''}${count > 0 && editorCount > 0 ? ' + ' : ''}${editorCount > 0 ? `${editorCount} editor` : ''} comments`}
    >
      {count > 0 && (
        <>
          <MessageSquare className="h-3.5 w-3.5 text-coral" />
          <span className="inline-flex items-center justify-center text-xs font-medium tabular-nums rounded-full px-1.5 py-0.5 bg-coral-light text-coral min-w-[20px]">
            {count}
          </span>
        </>
      )}
      {editorCount > 0 && (
        <>
          <FileEdit className="h-3.5 w-3.5 text-teal" />
          <span className="inline-flex items-center justify-center text-xs font-medium tabular-nums rounded-full px-1.5 py-0.5 bg-teal-light text-teal-dark min-w-[20px]">
            {editorCount}
          </span>
        </>
      )}
    </button>
  );
}

function WPStatusBadge({ status }: { status?: string | null }) {
  if (!status) return <Badge variant="outline">Unknown</Badge>;

  const map: Record<string, { variant: 'default' | 'success' | 'warning' | 'secondary'; label: string }> = {
    publish: { variant: 'success', label: 'Published' },
    published: { variant: 'success', label: 'Published' },
    draft: { variant: 'secondary', label: 'Draft' },
    review: { variant: 'warning', label: 'Review' },
  };

  const config = map[status.toLowerCase()] || { variant: 'outline' as const, label: status };
  return <Badge variant={config.variant}>{config.label}</Badge>;
}
